import React, { memo, useEffect, useMemo } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { Icon } from '../icons/Icon';
import ReactGA from 'react-ga4';
import { Link } from '../Link';
import { fixMojibake } from '../../utils/text/fix-mojibake';
import { CompanyLogo } from '../CompanyLogo';
import { CompanyName } from '../CompanyName';
import { trackJobClick } from '../../utils/analytics/analytics';
import { ApplicationStateSelector } from '../ApplicationStateSelector';
import { LazyTooltip } from '../Tooltip';
import { formatDateHydrationSafe } from '../../utils/date/date-formatter';
import { MatchDonutWithLoginPrompt } from '../MatchDonut';
import { useHitShared } from './hooks/useHitShared';
import { ApplicationStateMenu } from '../application-state';
import { useUserJobStateSync } from '../../hooks/useUserJobStateSync';
import { trackJobApply } from '../../utils/analytics/analytics';
import { getLocationDisplayWithCount, getSinglePrioritizedLocation } from '../location/utils/locationUtils';
import { isDark } from './utils/brandColorUtils';
import {
  COMPACT_TITLE_LINK_CLASS_DESKTOP,
  COMPACT_TITLE_LINK_CLASS_MOBILE,
  COMPACT_TITLE_SUBTITLE_CLASS,
  COMPACT_TITLE_SUBTITLE_CLASS_MOBILE,
} from './compactTitleClasses';
import type { HitProps } from './types';

/**
 * Compact variant of the Hit component
 * Displays job information in a horizontal, compact layout
 */
export const HitCompact: React.FC<HitProps> = memo(({ hit, sendEvent, index }) => {
  const intl = useIntl();
  const shared = useHitShared({ hit, sendEvent });
  const { setAppliedJobState, syncApplicationStateToBackend } = useUserJobStateSync();
  const menuButtonRefDesktop = React.useRef<HTMLButtonElement>(null);
  const menuButtonRefMobile = React.useRef<HTMLButtonElement>(null);
  
  const {
    jobId,
    jobUrl,
    articleId,
    titleId,
    jobTitle,
    isSaved,
    isApplied,
    isHidden,
    currentApplicationState,
    isToolsPage,
    savedJobPayload,
    brandColour,
    useBrandColour,
    leftBorderStyle,
    matchScore,
    matchScoreData,
    onLoginClick,
    actions,
    getUrlForIndexUiState,
    pushIndexUiState,
    makeRefinementState,
    showToast,
    browsedJobRef,
    menuRefDesktop,
    menuRefMobile,
    menuOpenSource,
    setMenuOpenSource,
    isMenuOpen,
    setIsMenuOpen,
    buttonState,
    locationData,
    filters,
  } = shared;

  // Track view event - use hit.objectID instead of hit object to avoid unnecessary re-runs
  const hitId = hit.objectID || hit.id;
  useEffect(() => {
    if (sendEvent) {
      sendEvent('view', hit, 'Job Viewed (CA English)');
    }
  }, [sendEvent, hitId]);

  // Memoize filter parsing to avoid JSON.parse on every render
  const parsedFilters = useMemo(() => {
    if (typeof filters === 'string') {
      try {
        return JSON.parse(filters) as Record<string, string[]>;
      } catch {
        return {};
      }
    }
    return (filters || {}) as Record<string, string[]>;
  }, [filters]);

  // Memoize expensive location calculations
  const userLocationFilter = useMemo(() => parsedFilters.location, [parsedFilters.location]);
  const singleLocation = useMemo(
    () => getSinglePrioritizedLocation(hit, userLocationFilter),
    [hit, userLocationFilter]
  );

  // Get location data for desktop layout - memoized
  const desktopLocationData = useMemo(() => getLocationDisplayWithCount(hit), [hit]);

  return (
    <article
      id={articleId}
      ref={browsedJobRef}
      className={`job-listing hit-card ${isMenuOpen ? 'hit-card--menu-open' : ''}`}
      title={jobTitle}
      aria-labelledby={titleId}
      style={{
        ...leftBorderStyle,
        position: 'relative',
        contentVisibility: 'auto',
        containIntrinsicSize: '1px 150px',
        contain: isMenuOpen ? 'layout style' : 'layout style paint',
      }}
    >
      <div
        className="hit-card__logo-desktop"
        role="button"
        tabIndex={0}
        title={intl.formatMessage({ id: 'hit.companyLinkTitle', defaultMessage: 'Search for jobs at {company}', description: 'Title attribute for company link in job card' }, { company: hit.companyId ?? '' })}
        onClick={() => hit.companyId && pushIndexUiState(makeRefinementState('companyId', hit.companyId))}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            hit.companyId && pushIndexUiState(makeRefinementState('companyId', hit.companyId));
          }
        }}
      >
        <CompanyLogo
          companyName={hit.companyId ?? ''}
          alt={hit.companyId ?? ''}
          isTyomarkkinatori={hit.isTyomarkkinatori}
          size={56}
          loading={index !== undefined && index < 3 ? 'eager' : 'lazy'}
        />
      </div>

      <MatchDonutWithLoginPrompt
        score={matchScore}
        size={48}
        onLoginClick={onLoginClick}
        breakdown={matchScoreData ?? undefined}
        className="hit-card__donut-desktop"
      />

      <div
        className="hit-card__logo-mobile"
        onClick={() => hit.companyId && pushIndexUiState(makeRefinementState('companyId', hit.companyId))}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            hit.companyId && pushIndexUiState(makeRefinementState('companyId', hit.companyId));
          }
        }}
        role="button"
        tabIndex={0}
        title={intl.formatMessage({ id: 'hit.companyLinkTitle', defaultMessage: 'Search for jobs at {company}', description: 'Title attribute for company link in job card' }, { company: hit.companyId ?? '' })}
      >
        <CompanyLogo
          companyName={hit.companyId ?? ''}
          alt={hit.companyId ?? ''}
          isTyomarkkinatori={hit.isTyomarkkinatori}
          size={56}
          loading={index !== undefined && index < 3 ? 'eager' : 'lazy'}
        />
        {hit.companyId ? (
          <span className="hit-card__company-mobile">
            <CompanyName
              name={(hit as { companyNameDisplay?: string }).companyNameDisplay ?? hit.companyId}
              className="font-bold text-xs md:text-sm text-gray-700 dark:text-gray-300"
            />
          </span>
        ) : null}
      </div>

      <div className="hit-card__main">
        <div className="hit-card__desktop-row">
          <div className="hit-card__title-block">
            {hit.companyId ? (
              <div
                className="hit-card__company-desktop"
                role="button"
                tabIndex={0}
                onClick={() => pushIndexUiState(makeRefinementState('companyId', hit.companyId ?? ''))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    pushIndexUiState(makeRefinementState('companyId', hit.companyId ?? ''));
                  }
                }}
              >
                <CompanyName
                  name={(hit as { companyNameDisplay?: string }).companyNameDisplay ?? hit.companyId}
                  className="font-bold text-sm text-gray-700 dark:text-gray-300"
                />
              </div>
            ) : null}
            <h3 id={titleId} className="sr-only">{jobTitle}</h3>
            <Link
              href={jobUrl}
              className={`${COMPACT_TITLE_LINK_CLASS_DESKTOP} mb-1`}
              onClick={(e) => {
                if (sendEvent) {
                  sendEvent('click', hit, 'Job Details Clicked');
                  sendEvent('conversion', hit, 'Job Details Clicked');
                }
                trackJobClick({
                  jobId: hit.objectID,
                  jobTitle: hit.normalisedTitle || hit.title || '',
                  companyId: hit.companyId || '',
                  locale: intl.locale,
                });
              }}
            >
              {fixMojibake(hit.normalisedTitle ?? '')}
            </Link>
            {hit.title && hit.title !== hit.normalisedTitle ? (
              <div className={COMPACT_TITLE_SUBTITLE_CLASS}>
                {fixMojibake(hit.title)}
              </div>
            ) : null}
            {isToolsPage && (
              <div className="mt-2">
                <ApplicationStateSelector
                  jobId={jobId}
                  currentState={currentApplicationState}
                  markAsAppliedOnStateSet={true}
                  jobData={savedJobPayload}
                  setAppliedJobState={setAppliedJobState}
                  syncApplicationStateToBackend={syncApplicationStateToBackend}
                  isApplied={isApplied}
                  trackJobApply={trackJobApply}
                  locale={intl.locale}
                />
              </div>
            )}
          </div>

          <div className="hit-card__pills-desktop">
            <div className="hit-card__pills-row">
              {desktopLocationData.allLocations.slice(0, 2).map((loc, idx) => {
                const locForUrl = desktopLocationData.allLocationsForUrl[idx] || loc;
                return (
                  <LazyTooltip
                    key={idx}
                    content={intl.formatMessage({ id: 'hit.tooltip.filterByLocation', defaultMessage: 'Filter jobs in: {locations}', description: 'Tooltip text for location link that filters search results to show only jobs in the specified location(s)' }, { locations: loc })}
                    position="top"
                  >
                    <Link
                      href={getUrlForIndexUiState(makeRefinementState('location', locForUrl))}
                      className="hit-pill hit-pill--green hover:bg-green-100 dark:hover:bg-green-900/50 transition-colors"
                      title={loc}
                      onClick={(e) => { e.preventDefault(); if (locForUrl) pushIndexUiState(makeRefinementState('location', locForUrl)); }}
                    >
                      <Icon name="mapPin" useMaskForColor className="hit-icon-pill" />
                      {fixMojibake(loc)}
                    </Link>
                  </LazyTooltip>
                );
              })}
              {desktopLocationData.allLocations.length > 2 && (
                <span className="hit-pill hit-pill--green">
                  <Icon name="mapPin" useMaskForColor className="hit-icon-pill" />
                  <FormattedMessage
                    id="hit.moreLocations"
                    defaultMessage="+{count} more"
                    values={{ count: desktopLocationData.allLocations.length - 2 }}
                    description="Text showing additional job locations when a job is available in more than 2 locations"
                  />
                </span>
              )}
            </div>
            <div className="hit-card__pills-row">
              {hit.remoteness && hit.remoteness.trim() !== '' && (
                <LazyTooltip
                  content={intl.formatMessage({ id: 'hit.tooltip.filterByRemoteness', defaultMessage: 'Filter jobs by remoteness: {remoteness}', description: 'Tooltip text for remoteness link that filters search results to show only jobs with the specified work model (remote, hybrid, onsite)' }, { remoteness: hit.remoteness })}
                  position="top"
                >
                  <Link
                    href={getUrlForIndexUiState(makeRefinementState('remoteness', hit.remoteness ?? ''))}
                    className="hit-pill hit-pill--blue hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                    onClick={(e) => { e.preventDefault(); pushIndexUiState(makeRefinementState('remoteness', hit.remoteness ?? '')); }}
                  >
                    <Icon name="wifi" useMaskForColor className="hit-icon-pill" />
                    {fixMojibake(hit.remoteness ?? '')}
                  </Link>
                </LazyTooltip>
              )}
              {hit.jobType && hit.jobType.trim() !== '' && (
                <LazyTooltip
                  content={intl.formatMessage({ id: 'hit.tooltip.filterByJobType', defaultMessage: 'Filter jobs by type: {jobType}', description: 'Tooltip text for job type link that filters search results to show only jobs of the specified type (full-time, part-time, etc.)' }, { jobType: hit.jobType })}
                  position="top"
                >
                  <Link
                    href={getUrlForIndexUiState(makeRefinementState('jobType', hit.jobType ?? ''))}
                    className="hit-pill hit-pill--purple hover:bg-purple-100 dark:hover:bg-purple-900/50 transition-colors"
                    onClick={(e) => { e.preventDefault(); pushIndexUiState(makeRefinementState('jobType', hit.jobType ?? '')); }}
                  >
                    <Icon name="briefcase" useMaskForColor className="hit-icon-pill" />
                    {fixMojibake(hit.jobType ?? '')}
                  </Link>
                </LazyTooltip>
              )}
              {hit.normalisedRoleLevel && hit.normalisedRoleLevel.trim() !== '' && (
                <LazyTooltip
                  content={intl.formatMessage({ id: 'hit.tooltip.filterByLevel', defaultMessage: 'Filter jobs by level: {level}', description: 'Tooltip text for role level link that filters search results to show only jobs at the specified experience level (junior, mid, senior, etc.)' }, { level: hit.normalisedRoleLevel })}
                  position="top"
                >
                  <Link
                    href={getUrlForIndexUiState(makeRefinementState('normalisedRoleLevel', hit.normalisedRoleLevel ?? ''))}
                    className="hit-pill hit-pill--orange hover:bg-orange-100 dark:hover:bg-orange-900/50 transition-colors"
                    onClick={(e) => { e.preventDefault(); pushIndexUiState(makeRefinementState('normalisedRoleLevel', hit.normalisedRoleLevel ?? '')); }}
                  >
                    {fixMojibake(hit.normalisedRoleLevel ?? '')}
                  </Link>
                </LazyTooltip>
              )}
              {hit.requiredLanguages && Array.isArray(hit.requiredLanguages) && hit.requiredLanguages.length > 0 && (
                <LazyTooltip
                  content={intl.formatMessage(
                    { id: 'hit.tooltip.filterByLanguages', defaultMessage: 'Filter jobs by required languages: {languages}', description: 'Tooltip text for languages link that filters search results to show only jobs requiring the specified languages' },
                    { languages: hit.requiredLanguages.join(', ') }
                  )}
                  position="top"
                >
                  <div className="hit-pill hit-pill--indigo">
                    <Icon name="globeAlt" useMaskForColor className="hit-icon-pill" />
                    <span className="hit-pill__truncate">
                      {hit.requiredLanguages.slice(0, 2).map((lang: string, idx: number) => (
                        <span key={lang + idx}>
                          {fixMojibake(lang)}
                          {idx < Math.min(hit.requiredLanguages?.length || 0, 2) - 1 && ', '}
                        </span>
                      ))}
                      {hit.requiredLanguages.length > 2 && ` +${hit.requiredLanguages.length - 2}`}
                    </span>
                  </div>
                </LazyTooltip>
              )}
            </div>
          </div>

          <div className="hit-card__actions-desktop">
            <footer className="hit-card__salary-block">
              <span className="hit-card__salary-value">
                {hit._salaryLabel
                  ? hit._salaryLabel
                  : typeof hit.salaryRangeMin === 'number' && typeof hit.salaryRangeMax === 'number' && hit.salaryRangeMin > 0 && hit.salaryRangeMax > 0
                  ? `€${hit.salaryRangeMin.toLocaleString()} - €${hit.salaryRangeMax.toLocaleString()}`
                  : intl.formatMessage({ id: 'hit.salaryNotSpecified.card', defaultMessage: 'Salary not specified', description: 'Shown when salary is not specified in job card' })}
              </span>
              <div className="hit-card__meta-text">
                {(hit._lastSeenLabel || hit.lastSeen) && (
                  hit._lastSeenLabel ? (
                    <span className="hit-card__meta-row">
                      <Icon name="clock" useMaskForColor className="hit-icon-pill hit-icon-btn--muted" />
                      {hit._lastSeenLabel}
                    </span>
                  ) : (() => {
                    const now = new Date();
                    const lastSeenDate = new Date(hit.lastSeen!);
                    const diffMs = now.getTime() - lastSeenDate.getTime();
                    const diffMins = Math.floor(diffMs / 60000);
                    const diffHours = Math.floor(diffMins / 60);
                    const diffDays = Math.floor(diffHours / 24);
                    let freshnessId = 'hit.lastSeen.daysAgo';
                    let freshnessValue: number = diffDays;
                    if (diffMins < 120) {
                      freshnessId = 'hit.lastSeen.minutesAgo';
                      freshnessValue = diffMins;
                    } else if (diffHours < 24) {
                      freshnessId = 'hit.lastSeen.hoursAgo';
                      freshnessValue = diffHours;
                    }
                    return (
                      <span className="hit-card__meta-row">
                        <Icon name="clock" useMaskForColor className="hit-icon-pill hit-icon-btn--muted" />
                        <FormattedMessage
                          id={freshnessId}
                          defaultMessage={
                            freshnessId === 'hit.lastSeen.minutesAgo'
                              ? '{value, plural, one {{value} minute ago} other {{value} minutes ago}}'
                              : freshnessId === 'hit.lastSeen.hoursAgo'
                              ? '{value, plural, one {{value} hour ago} other {{value} hours ago}}'
                              : '{value, plural, one {{value} day ago} other {{value} days ago}}'
                          }
                          values={{ value: freshnessValue }}
                          description="Indicates how recently the job was last seen/crawled."
                        />
                      </span>
                    );
                  })()
                )}
                {hit.postedDate && (() => {
                  const formatted = formatDateHydrationSafe(hit.postedDate, intl.locale, { year: 'numeric', month: 'short', day: 'numeric' }) || String(hit.postedDate);
                  return (
                    <span className="block">
                      <FormattedMessage 
                        id="hit.postedDate" 
                        defaultMessage="Posted: {date}" 
                        values={{ date: formatted }}
                        description="Job posting date shown in job card"
                      />
                    </span>
                  );
                })()}
                {hit.applicationDeadline && (() => {
                  const formatted = formatDateHydrationSafe(hit.applicationDeadline, intl.locale, { year: 'numeric', month: 'short', day: 'numeric' }) || String(hit.applicationDeadline);
                  return (
                    <span className="block">
                      <FormattedMessage 
                        id="hit.applicationDeadline" 
                        defaultMessage="Deadline: {date}" 
                        values={{ date: formatted }}
                        description="Application deadline shown in job card"
                      />
                    </span>
                  );
                })()}
              </div>
            </footer>

            <div className="hit-card__actions-inner relative" ref={menuRefDesktop}>
              <LazyTooltip
                content={isSaved
                  ? intl.formatMessage({ id: 'hit.tooltip.saved', defaultMessage: 'Remove from saved jobs', description: 'Tooltip text for save button when job is already saved, indicating clicking will remove it from saved jobs' })
                  : intl.formatMessage({ id: 'hit.tooltip.save', defaultMessage: 'Save this job to compare with others', description: 'Tooltip text for save button when job is not saved, indicating clicking will save the job' })}
                position="top"
              >
                <button
                  className={`hit-btn-save ${isSaved ? '' : 'hit-btn-save--unsaved hover:bg-gray-100 dark:hover:bg-gray-700'} transition cursor-pointer`}
                  aria-label={
                    isSaved
                      ? intl.formatMessage({ id: 'hit.saved', defaultMessage: 'Saved', description: 'Button label when job is saved' })
                      : intl.formatMessage({ id: 'hit.save', defaultMessage: 'Save', description: 'Button label to save a job' })
                  }
                  style={isSaved ? {
                    backgroundColor: useBrandColour && brandColour ? brandColour : '#bbf7d0',
                    borderColor: useBrandColour && brandColour ? brandColour : '#bbf7d0',
                  } : {}}
                  onClick={actions.handleToggleSaved}
                  type="button"
                >
                  {isSaved ? (
                    <Icon name="bookmark" variant="solid" useMaskForColor className="hit-icon-btn" style={{ color: useBrandColour && brandColour ? (isDark(brandColour) ? '#fff' : '#111827') : '#111827' }} />
                  ) : (
                    <Icon name="bookmark" useMaskForColor className="hit-icon-btn hit-icon-btn--muted" />
                  )}
                </button>
              </LazyTooltip>

              <LazyTooltip
                content={buttonState
                  ? buttonState.label
                  : intl.formatMessage({ id: 'hit.tooltip.moreOptions', defaultMessage: 'More options' })}
                position="top"
                showOnFocus={false}
              >
                <button
                  ref={menuButtonRefDesktop}
                    className={`hit-btn-menu ${buttonState ? 'hit-btn-menu--with-state' : 'hit-btn-menu--compact hit-btn-menu--default'} ${
                      buttonState
                        ? buttonState.color === 'purple'
                          ? 'bg-purple-50 dark:bg-purple-900/30 border-purple-200 dark:border-purple-700'
                          : buttonState.color === 'green'
                          ? 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-700'
                          : buttonState.color === 'red'
                          ? 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-700'
                          : buttonState.color === 'emerald'
                          ? 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-200 dark:border-emerald-700'
                          : 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-700'
                        : ''
                    } hover:bg-gray-100 dark:hover:bg-gray-700 transition cursor-pointer`}
                    aria-label={buttonState 
                      ? buttonState.label
                      : intl.formatMessage({ id: 'hit.moreOptions', defaultMessage: 'More options', description: 'Button label for menu with additional job actions (save, hide, mark as applied, etc.)' })}
                    onClick={() => setMenuOpenSource((prev) => (prev === 'desktop' ? null : 'desktop'))}
                    type="button"
                  >
                    {buttonState ? (
                      <>
                        <Icon name={buttonState.icon} variant="solid" useMaskForColor className={`hit-icon-btn ${
                          buttonState.color === 'purple'
                            ? 'text-purple-700 dark:text-purple-300'
                            : buttonState.color === 'green'
                            ? 'text-green-700 dark:text-green-300'
                            : buttonState.color === 'red'
                            ? 'text-red-700 dark:text-red-300'
                            : buttonState.color === 'emerald'
                            ? 'text-emerald-700 dark:text-emerald-300'
                            : 'text-blue-700 dark:text-blue-300'
                        }`} />
                        <Icon name="ellipsisVertical" useMaskForColor className="hit-icon-btn hit-icon-btn--muted" />
                      </>
                    ) : (
                      <Icon name="ellipsisVertical" useMaskForColor className="hit-icon-btn hit-icon-btn--muted" />
                    )}
                  </button>
              </LazyTooltip>

              <LazyTooltip
                content={intl.formatMessage({ id: 'hit.tooltip.details', defaultMessage: 'View full job details and description', description: 'Tooltip text for button/link that opens the full job detail page with complete job description' })}
                position="top"
              >
                <Link
                  className="hit-btn-details-desktop"
                  style={{
                    backgroundColor: useBrandColour && brandColour ? brandColour : '#bbf7d0',
                    color: useBrandColour && brandColour ? (isDark(brandColour) ? '#fff' : '#111827') : '#111827',
                    border: 'none',
                  }}
                  href={jobUrl}
                  onClick={(e) => {
                    if (sendEvent) {
                      sendEvent('click', hit, 'Job Details Clicked');
                      sendEvent('conversion', hit, 'Job Details Clicked');
                    }
                    ReactGA.event({ category: 'interactions', action: 'click_see_ad', label: hit.objectID, nonInteraction: false });
                  }}
                >
                  {intl.formatMessage({ id: 'hit.seeAd', defaultMessage: 'Details', description: 'Button label to view job details' })}
                </Link>
              </LazyTooltip>
            </div>
          </div>
        </div>

        <div className="hit-card__mobile-col">
          <div className="hit-card__title-block">
            <Link
              href={jobUrl}
              className={`${COMPACT_TITLE_LINK_CLASS_MOBILE} mb-0.5`}
              onClick={(e) => {
                if (sendEvent) {
                  sendEvent('click', hit, 'Job Details Clicked');
                  sendEvent('conversion', hit, 'Job Details Clicked');
                }
                trackJobClick({
                  jobId: hit.objectID,
                  jobTitle: hit.normalisedTitle || hit.title || '',
                  companyId: hit.companyId || '',
                  locale: intl.locale,
                });
              }}
            >
              {fixMojibake(hit.normalisedTitle ?? '')}
            </Link>
            {hit.title && hit.title !== hit.normalisedTitle ? (
              <div className={COMPACT_TITLE_SUBTITLE_CLASS_MOBILE}>
                {fixMojibake(hit.title)}
              </div>
            ) : null}
            {isToolsPage && (
              <div className="mt-2">
                <ApplicationStateSelector
                  jobId={jobId}
                  currentState={currentApplicationState}
                  markAsAppliedOnStateSet={true}
                  jobData={savedJobPayload}
                  setAppliedJobState={setAppliedJobState}
                  syncApplicationStateToBackend={syncApplicationStateToBackend}
                  isApplied={isApplied}
                  trackJobApply={trackJobApply}
                  locale={intl.locale}
                />
              </div>
            )}
          </div>

          {singleLocation.display ? (
            <div className="hit-pills-grid">
              <LazyTooltip
                content={intl.formatMessage({ id: 'hit.tooltip.filterByLocation', defaultMessage: 'Filter jobs in: {locations}', description: 'Tooltip text for location link that filters search results to show only jobs in the specified location(s)' }, { locations: singleLocation.display })}
                position="top"
              >
                <Link
                  href={getUrlForIndexUiState(makeRefinementState('location', singleLocation.locationForUrl))}
                  className="hit-pill-mobile hit-pill-mobile--green hover:bg-green-100 dark:hover:bg-green-900/50 transition-colors"
                  title={singleLocation.fullList}
                  onClick={(e) => { e.preventDefault(); if (singleLocation.locationForUrl) pushIndexUiState(makeRefinementState('location', singleLocation.locationForUrl)); }}
                >
                  <Icon name="mapPin" useMaskForColor className="hit-icon-pill-mobile" />
                  <span className="hit-pill__center">{fixMojibake(singleLocation.display)}</span>
                </Link>
              </LazyTooltip>
              {hit.remoteness && hit.remoteness.trim() !== '' && (
                <LazyTooltip
                  content={intl.formatMessage({ id: 'hit.tooltip.filterByRemoteness', defaultMessage: 'Filter jobs by remoteness: {remoteness}', description: 'Tooltip text for remoteness link that filters search results to show only jobs with the specified work model (remote, hybrid, onsite)' }, { remoteness: hit.remoteness })}
                  position="top"
                >
                  <Link
                    href={getUrlForIndexUiState(makeRefinementState('remoteness', hit.remoteness ?? ''))}
                    className="hit-pill-mobile hit-pill-mobile--blue hit-pill-mobile--w-auto hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                    onClick={(e) => { e.preventDefault(); pushIndexUiState(makeRefinementState('remoteness', hit.remoteness ?? '')); }}
                  >
                    <Icon name="wifi" useMaskForColor className="hit-icon-pill-mobile" />
                    <span className="hit-pill__center">{fixMojibake(hit.remoteness ?? '')}</span>
                  </Link>
                </LazyTooltip>
              )}
              {hit.jobType && hit.jobType.trim() !== '' && (
                <LazyTooltip
                  content={intl.formatMessage({ id: 'hit.tooltip.filterByJobType', defaultMessage: 'Filter jobs by type: {jobType}', description: 'Tooltip text for job type link that filters search results to show only jobs of the specified type (full-time, part-time, etc.)' }, { jobType: hit.jobType })}
                  position="top"
                >
                  <Link
                    href={getUrlForIndexUiState(makeRefinementState('jobType', hit.jobType ?? ''))}
                    className="hit-pill-mobile hit-pill-mobile--purple hit-pill-mobile--w-auto hover:bg-purple-100 dark:hover:bg-purple-900/50 transition-colors"
                    onClick={(e) => { e.preventDefault(); pushIndexUiState(makeRefinementState('jobType', hit.jobType ?? '')); }}
                  >
                    <Icon name="briefcase" useMaskForColor className="hit-icon-pill-mobile" />
                    <span className="hit-pill__center">{fixMojibake(hit.jobType ?? '')}</span>
                  </Link>
                </LazyTooltip>
              )}
              {hit.normalisedRoleLevel && hit.normalisedRoleLevel.trim() !== '' && (
                <LazyTooltip
                  content={intl.formatMessage({ id: 'hit.tooltip.filterByLevel', defaultMessage: 'Filter jobs by level: {level}', description: 'Tooltip text for role level link that filters search results to show only jobs at the specified experience level (junior, mid, senior, etc.)' }, { level: hit.normalisedRoleLevel })}
                  position="top"
                >
                  <Link
                    href={getUrlForIndexUiState(makeRefinementState('normalisedRoleLevel', hit.normalisedRoleLevel ?? ''))}
                    className="hit-pill-mobile--orange-md-only hover:bg-orange-100 dark:hover:bg-orange-900/50 transition-colors"
                    onClick={(e) => { e.preventDefault(); pushIndexUiState(makeRefinementState('normalisedRoleLevel', hit.normalisedRoleLevel ?? '')); }}
                  >
                    <span className="hit-pill__center">
                      {fixMojibake(hit.normalisedRoleLevel ?? '')}
                    </span>
                  </Link>
                </LazyTooltip>
              )}
              {hit.requiredLanguages && Array.isArray(hit.requiredLanguages) && hit.requiredLanguages.length > 0 && (
                <LazyTooltip
                  content={intl.formatMessage(
                    { id: 'hit.tooltip.filterByLanguages', defaultMessage: 'Filter jobs by required languages: {languages}', description: 'Tooltip text for languages link that filters search results to show only jobs requiring the specified languages' },
                    { languages: hit.requiredLanguages.join(', ') }
                  )}
                  position="top"
                >
                  <div className="hit-pill-mobile hit-pill-mobile--indigo hit-pill-mobile--w-auto">
                    <Icon name="globeAlt" useMaskForColor className="hit-icon-pill-mobile" />
                    <span className="hit-pill__center">
                      {hit.requiredLanguages.slice(0, 2).map((lang: string, idx: number) => (
                        <span key={lang + idx}>
                          {fixMojibake(lang)}
                          {idx < Math.min(hit.requiredLanguages?.length || 0, 2) - 1 && ', '}
                        </span>
                      ))}
                      {hit.requiredLanguages.length > 2 && ` +${hit.requiredLanguages.length - 2}`}
                    </span>
                  </div>
                </LazyTooltip>
              )}
              <span className="hit-pill-mobile hit-pill-mobile--gray lg:hidden">
                {hit._salaryLabel
                  ? hit._salaryLabel
                  : typeof hit.salaryRangeMin === 'number' && typeof hit.salaryRangeMax === 'number' && hit.salaryRangeMin > 0 && hit.salaryRangeMax > 0
                  ? `€${hit.salaryRangeMin.toLocaleString()} - €${hit.salaryRangeMax.toLocaleString()}`
                  : intl.formatMessage({ id: 'hit.salaryNotSpecified.card', defaultMessage: 'Salary not specified', description: 'Shown when salary is not specified in job card' })}
              </span>
              {(hit._lastSeenLabel || hit.lastSeen) && (
                  hit._lastSeenLabel ? (
                  <span className="hit-pill-mobile hit-pill-mobile--gray-muted lg:hidden">
                    <Icon name="clock" useMaskForColor className="hit-icon-pill-mobile" />
                    {hit._lastSeenLabel}
                  </span>
                ) : (() => {
                  const now = new Date();
                  const lastSeenDate = new Date(hit.lastSeen!);
                  const diffMs = now.getTime() - lastSeenDate.getTime();
                  const diffMins = Math.floor(diffMs / 60000);
                  const diffHours = Math.floor(diffMins / 60);
                  const diffDays = Math.floor(diffHours / 24);
                  let freshnessId = 'hit.lastSeen.daysAgo';
                  let freshnessValue: number = diffDays;
                  if (diffMins < 120) {
                    freshnessId = 'hit.lastSeen.minutesAgo';
                    freshnessValue = diffMins;
                  } else if (diffHours < 24) {
                    freshnessId = 'hit.lastSeen.hoursAgo';
                    freshnessValue = diffHours;
                  }
                  return (
                    <span className="hit-pill-mobile hit-pill-mobile--gray-muted lg:hidden">
                      <Icon name="clock" useMaskForColor className="hit-icon-pill-mobile" />
                      <FormattedMessage
                        id={freshnessId}
                        defaultMessage={
                          freshnessId === 'hit.lastSeen.minutesAgo'
                            ? '{value, plural, one {{value} minute ago} other {{value} minutes ago}}'
                            : freshnessId === 'hit.lastSeen.hoursAgo'
                            ? '{value, plural, one {{value} hour ago} other {{value} hours ago}}'
                            : '{value, plural, one {{value} day ago} other {{value} days ago}}'
                        }
                        values={{ value: freshnessValue }}
                        description="Indicates how recently the job was last seen/crawled."
                      />
                    </span>
                  );
                })()
              )}
            </div>
          ) : (
            <div className="hit-pills-grid">
              {hit.remoteness && hit.remoteness.trim() !== '' && (
                <LazyTooltip
                  content={intl.formatMessage({ id: 'hit.tooltip.filterByRemoteness', defaultMessage: 'Filter jobs by remoteness: {remoteness}', description: 'Tooltip text for remoteness link that filters search results to show only jobs with the specified work model (remote, hybrid, onsite)' }, { remoteness: hit.remoteness })}
                  position="top"
                >
                  <Link
                    href={getUrlForIndexUiState(makeRefinementState('remoteness', hit.remoteness ?? ''))}
                    className="hit-pill-mobile hit-pill-mobile--blue hit-pill-mobile--w-auto hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                    onClick={(e) => { e.preventDefault(); pushIndexUiState(makeRefinementState('remoteness', hit.remoteness ?? '')); }}
                  >
                    <Icon name="wifi" useMaskForColor className="hit-icon-pill-mobile" />
                    <span className="hit-pill__center">{fixMojibake(hit.remoteness ?? '')}</span>
                  </Link>
                </LazyTooltip>
              )}
              {hit.jobType && hit.jobType.trim() !== '' && (
                <LazyTooltip
                  content={intl.formatMessage({ id: 'hit.tooltip.filterByJobType', defaultMessage: 'Filter jobs by type: {jobType}', description: 'Tooltip text for job type link that filters search results to show only jobs of the specified type (full-time, part-time, etc.)' }, { jobType: hit.jobType })}
                  position="top"
                >
                  <Link
                    href={getUrlForIndexUiState(makeRefinementState('jobType', hit.jobType ?? ''))}
                    className="hit-pill-mobile hit-pill-mobile--purple hit-pill-mobile--w-auto hover:bg-purple-100 dark:hover:bg-purple-900/50 transition-colors"
                    onClick={(e) => { e.preventDefault(); pushIndexUiState(makeRefinementState('jobType', hit.jobType ?? '')); }}
                  >
                    <Icon name="briefcase" useMaskForColor className="hit-icon-pill-mobile" />
                    <span className="hit-pill__center">{fixMojibake(hit.jobType ?? '')}</span>
                  </Link>
                </LazyTooltip>
              )}
            </div>
          )}

          <div className="hit-card__actions-mobile" ref={menuRefMobile}>
            <MatchDonutWithLoginPrompt
              score={matchScore}
              size={40}
              onLoginClick={onLoginClick}
              breakdown={matchScoreData ?? undefined}
              className="hit-card__donut-mobile"
            />
            <div className="hit-card__actions-mobile-inner">
              <LazyTooltip
                content={isSaved
                  ? intl.formatMessage({ id: 'hit.tooltip.saved', defaultMessage: 'Remove from saved jobs', description: 'Tooltip text for save button when job is already saved, indicating clicking will remove it from saved jobs' })
                  : intl.formatMessage({ id: 'hit.tooltip.save', defaultMessage: 'Save this job to compare with others', description: 'Tooltip text for save button when job is not saved, indicating clicking will save the job' })}
                position="top"
              >
                <button
                  className={`hit-btn-save ${isSaved ? '' : 'hit-btn-save--unsaved hover:bg-gray-100 dark:hover:bg-gray-700'} transition cursor-pointer`}
                  aria-label={
                    isSaved
                      ? intl.formatMessage({ id: 'hit.saved', defaultMessage: 'Saved', description: 'Button label when job is saved' })
                      : intl.formatMessage({ id: 'hit.save', defaultMessage: 'Save', description: 'Button label to save a job' })
                  }
                  style={isSaved ? {
                    backgroundColor: useBrandColour && brandColour ? brandColour : '#bbf7d0',
                    borderColor: useBrandColour && brandColour ? brandColour : '#bbf7d0',
                  } : {}}
                  onClick={actions.handleToggleSaved}
                  type="button"
                >
                  {isSaved ? (
                    <Icon name="bookmark" variant="solid" useMaskForColor className="hit-icon-btn" style={{ color: useBrandColour && brandColour ? (isDark(brandColour) ? '#fff' : '#111827') : '#111827' }} />
                  ) : (
                    <Icon name="bookmark" useMaskForColor className="hit-icon-btn hit-icon-btn--muted" />
                  )}
                </button>
              </LazyTooltip>

              <LazyTooltip
                  content={buttonState
                    ? buttonState.label
                    : intl.formatMessage({ id: 'hit.tooltip.moreOptions', defaultMessage: 'More options' })}
                  position="top"
                  showOnFocus={false}
                >
                  <button
                    ref={menuButtonRefMobile}
                    className={`hit-btn-menu ${buttonState ? 'hit-btn-menu--with-state w-auto px-1.5 md:px-2' : 'hit-btn-menu--compact hit-btn-menu--default'} ${
                      buttonState
                        ? buttonState.color === 'purple'
                          ? 'bg-purple-50 dark:bg-purple-900/30 border-purple-200 dark:border-purple-700'
                          : buttonState.color === 'green'
                          ? 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-700'
                          : buttonState.color === 'red'
                          ? 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-700'
                          : buttonState.color === 'emerald'
                          ? 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-200 dark:border-emerald-700'
                          : 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-700'
                        : ''
                    } hover:bg-gray-100 dark:hover:bg-gray-700 transition cursor-pointer`}
                    aria-label={buttonState
                      ? buttonState.label
                      : intl.formatMessage({ id: 'hit.moreOptions', defaultMessage: 'More options' })}
                    onClick={() => setMenuOpenSource((prev) => (prev === 'mobile' ? null : 'mobile'))}
                    type="button"
                  >
                    {buttonState ? (
                      <>
                        <Icon name={buttonState.icon} variant="solid" useMaskForColor className={`hit-icon-btn ${
                          buttonState.color === 'purple'
                            ? 'text-purple-700 dark:text-purple-300'
                            : buttonState.color === 'green'
                            ? 'text-green-700 dark:text-green-300'
                            : buttonState.color === 'red'
                            ? 'text-red-700 dark:text-red-300'
                            : buttonState.color === 'emerald'
                            ? 'text-emerald-700 dark:text-emerald-300'
                            : 'text-blue-700 dark:text-blue-300'
                        }`} />
                        <Icon name="ellipsisVertical" useMaskForColor className="hit-icon-btn hit-icon-btn--muted" />
                      </>
                    ) : (
                      <Icon name="ellipsisVertical" useMaskForColor className="hit-icon-btn hit-icon-btn--muted" />
                    )}
                  </button>
                </LazyTooltip>
              <LazyTooltip
                content={intl.formatMessage({ id: 'hit.tooltip.details', defaultMessage: 'View full job details and description', description: 'Tooltip text for button/link that opens the full job detail page with complete job description' })}
                position="top"
              >
                <Link
                  className="hit-btn-details-mobile"
                  style={{
                    backgroundColor: useBrandColour && brandColour ? brandColour : '#bbf7d0',
                    color: useBrandColour && brandColour ? (isDark(brandColour) ? '#fff' : '#111827') : '#111827',
                    border: 'none',
                  }}
                  href={jobUrl}
                  onClick={(e) => {
                    if (sendEvent) {
                      sendEvent('click', hit, 'Job Details Clicked');
                      sendEvent('conversion', hit, 'Job Details Clicked');
                    }
                    ReactGA.event({ category: 'interactions', action: 'click_see_ad', label: hit.objectID, nonInteraction: false });
                  }}
                >
                  {intl.formatMessage({ id: 'hit.seeAd', defaultMessage: 'Details', description: 'Button label to view job details' })}
                </Link>
              </LazyTooltip>
            </div>
          </div>
        </div>
      </div>

      {/* Single dropdown instance – positioned by the button that opened it (desktop or mobile) */}
      {menuOpenSource && (
        <ApplicationStateMenu
          currentApplicationState={currentApplicationState}
          isApplied={isApplied}
          isHidden={isHidden}
          onSetApplicationState={actions.handleSetApplicationState}
          onToggleApplied={actions.handleToggleApplied}
          onToggleHide={actions.handleToggleHide}
          onClose={() => setMenuOpenSource(null)}
          className={menuOpenSource === 'mobile' ? 'w-56' : ''}
          triggerRef={menuOpenSource === 'desktop' ? menuButtonRefDesktop : menuButtonRefMobile}
          usePortal={true}
        />
      )}
    </article>
  );
});

HitCompact.displayName = 'HitCompact';
